/**
 * 响应式筛选工作台表格组件
 * 
 * 功能：
 * - 桌面端：完整表格视图（UnifiedDataTable）
 * - 移动端：卡片列表视图
 * - 平板端：简化表格视图（只显示核心列）
 */

import React from 'react';
import { Card, List, Space, Button, Tooltip, Tag, Progress, Typography, Empty } from 'antd';
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { InitialScreeningQuestionnaire } from '../../types/questionnaire';
import { UnifiedStatusTag, UnifiedDataTable } from '../unified';
import type { UnifiedTableColumn } from '../unified/UnifiedDataTable';
import { useResponsive } from '../../hooks/useResponsive';
import { EMERALD_THEME } from '../../config/designTokens';
import { getIndustryText, getCreditRatingText } from '../../utils/questionnaireEnumMapping';
import '../../styles/emerald-table.css';

const { Text, Title } = Typography;

interface ResponsiveScreeningTableProps {
  questionnaires: InitialScreeningQuestionnaire[];
  selectedQuestionnaires: string[];
  loading: boolean;
  onSelectionChange: (selectedRowKeys: React.Key[]) => void;
  onViewDetails: (questionnaire: InitialScreeningQuestionnaire) => void;
  onReview: (questionnaire: InitialScreeningQuestionnaire) => void;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize?: number) => void;
  };
}

export const ResponsiveScreeningTable: React.FC<ResponsiveScreeningTableProps> = ({
  questionnaires,
  selectedQuestionnaires,
  loading,
  onSelectionChange,
  onViewDetails,
  onReview,
  pagination,
}) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();

  // 移动端：卡片列表视图
  if (isMobile) {
    return (
      <List
        loading={loading}
        dataSource={questionnaires}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('screening:table.noQuestionnaireData')}
              style={{ padding: '40px 0' }}
            />
          ),
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: pagination.onChange,
          showSizeChanger: false,
          showQuickJumper: false,
          simple: true,
        }}
        renderItem={(item) => (
          <Card
            key={item._id}
            style={{
              marginBottom: EMERALD_THEME.spacing.md,
              borderRadius: EMERALD_THEME.borderRadius.lg,
              border: `1px solid ${EMERALD_THEME.colors.functional.border}`,
              boxShadow: EMERALD_THEME.shadows.sm,
            }}
            bodyStyle={{ padding: EMERALD_THEME.spacing.md }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {/* 标题行 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                    {item.projectName}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {item.issuerBasicInfo?.issuerName}
                  </Text>
                </div>
                <UnifiedStatusTag 
                  type="questionnaire" 
                  status={item.status as any}
                  size="small"
                />
              </div>

              {/* 信息行 */}
              <div style={{
                padding: `${EMERALD_THEME.spacing.sm}px 0`
              }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>{t('screening:table.aiScore')}</Text>
                <div style={{ marginTop: 4 }}>
                  <Progress
                    percent={item.aiScore || 0}
                    size="small"
                    strokeColor="#10b981"
                    showInfo={true}
                    format={(percent) => `${percent}`}
                  />
                </div>
              </div>

              {/* 操作按钮 */}
              <div style={{ 
                borderTop: `1px solid ${EMERALD_THEME.colors.functional.border}`,
                paddingTop: EMERALD_THEME.spacing.sm,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {item.submittedAt ? new Date(item.submittedAt).toLocaleDateString() : '-'}
                </Text>
                <Space size="small">
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => onViewDetails(item)}
                  >
                    {t('screening:table.details')}
                  </Button>
                  {item.status === 'submitted' && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => onReview(item)}
                    >
                      {t('screening:table.review')}
                    </Button>
                  )}
                </Space>
              </div>
            </Space>
          </Card>
        )}
      />
    );
  }

  // 桌面端和平板端：表格视图（响应式列配置）
  const getResponsiveColumns = (): UnifiedTableColumn<InitialScreeningQuestionnaire>[] => {
    // 核心列（始终显示）
    const coreColumns: UnifiedTableColumn<InitialScreeningQuestionnaire>[] = [
      {
        title: t('screening:table.projectName'),
        dataIndex: 'projectName',
        key: 'projectName',
        width: 280,
        minWidth: 200,
        fixed: isTablet ? undefined : 'left',
        ellipsis: { showTitle: false },
        render: (text: string) => (
          <Tooltip title={text} placement="topLeft">
            <span style={{ fontWeight: 500 }}>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: t('screening:table.issuer'),
        dataIndex: ['issuerBasicInfo', 'issuerName'],
        key: 'issuerName',
        width: 220,
        minWidth: 150,
        ellipsis: { showTitle: false },
        render: (text: string) => (
          <Tooltip title={text} placement="topLeft">
            <span>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: t('screening:table.aiScore'),
        dataIndex: 'aiScore',
        key: 'aiScore',
        width: 120,
        render: (score: number) => (
          <div style={{ width: 80 }}>
            <Progress
              percent={score}
              size="small"
              strokeColor="#10b981"
              showInfo={true}
              format={(percent) => `${percent}`}
            />
          </div>
        ),
      },
      {
        title: t('screening:table.status'),
        dataIndex: 'status',
        key: 'status',
        width: 140,
        ellipsis: true,
        render: (status: string) => {
          const statusKey = `screening:questionnaireStatus.${status}`;
          const statusText = t(statusKey);
          const tooltipText = statusText !== statusKey ? statusText : status;
          return (
            <Tooltip title={tooltipText}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <UnifiedStatusTag
                  type="questionnaire"
                  status={status as any}
                  size="small"
                />
              </div>
            </Tooltip>
          );
        },
      },
    ];

    // 扩展列（仅桌面端显示）
    const extendedColumns: UnifiedTableColumn<InitialScreeningQuestionnaire>[] = !isTablet ? [
      {
        title: t('screening:table.industry'),
        dataIndex: ['issuerRatingScale', 'industry'],
        key: 'industry',
        width: 130,
        ellipsis: true,
        render: (industry: string) => {
          const i18nKey = `screening:industry.${industry}`;
          const translated = t(i18nKey);
          const displayText = translated !== i18nKey ? translated : getIndustryText(industry);
          return <Tooltip title={displayText}><span>{displayText}</span></Tooltip>;
        },
      },
      {
        title: t('screening:table.creditRating'),
        dataIndex: ['issuerRatingScale', 'creditRating'],
        key: 'creditRating',
        width: 110,
        ellipsis: true,
        render: (rating: string) => {
          const i18nKey = `screening:creditRating.${rating}`;
          const translated = t(i18nKey);
          const displayText = translated !== i18nKey ? translated : getCreditRatingText(rating);
          return <Tooltip title={displayText}><Tag color="blue">{displayText}</Tag></Tooltip>;
        },
      },
      {
        title: t('screening:table.submitTime'),
        dataIndex: 'submittedAt',
        key: 'submittedAt',
        width: 120,
        render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
      },
    ] : [];

    // 操作列（始终显示）
    const actionColumn: UnifiedTableColumn<InitialScreeningQuestionnaire> = {
      title: t('screening:table.action'),
      key: 'actions',
      width: isTablet ? 120 : 200,
      fixed: isTablet ? undefined : 'right',
      render: (_, record: InitialScreeningQuestionnaire) => (
        <Space size="small">
          <Tooltip title={t('screening:table.viewDetails')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails(record)}
              style={{
                color: '#10b981',
                borderColor: '#10b981',
              }}
            />
          </Tooltip>
          {record.status === 'submitted' && (
            <>
              <Tooltip title={t('screening:table.reviewApprove')}>
                <Button
                  type="text"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => onReview(record)}
                  style={{
                    color: '#52c41a',
                    borderColor: '#52c41a',
                  }}
                />
              </Tooltip>
              {!isTablet && (
                <Tooltip title={t('screening:table.reviewReject')}>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => onReview(record)}
                    style={{
                      color: '#ff4d4f',
                      borderColor: '#ff4d4f',
                    }}
                  />
                </Tooltip>
              )}
            </>
          )}
        </Space>
      ),
    };

    return [...coreColumns, ...extendedColumns, actionColumn];
  };

  return (
    <UnifiedDataTable<InitialScreeningQuestionnaire>
      columns={getResponsiveColumns()}
      dataSource={questionnaires}
      rowKey="_id"
      loading={loading}
      rowSelection={{
        selectedRowKeys: selectedQuestionnaires,
        onChange: onSelectionChange,
        getCheckboxProps: (record) => ({
          disabled: record.status === 'approved' || record.status === 'rejected',
        }),
      }}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: pagination.onChange,
        showSizeChanger: !isTablet,
        showQuickJumper: !isTablet,
        showTotal: (total, range) =>
          t('screening:table.paginationInfo', { start: range[0], end: range[1], total }),
        pageSizeOptions: ['10', '20', '50', '100'],
        simple: isTablet,
      }}
      scroll={{ x: isTablet ? 800 : 1200 }}
      size={isTablet ? 'small' : 'middle'}
    />
  );
};

export default ResponsiveScreeningTable;

